import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Volume2, VolumeX, Loader2 } from "lucide-react";
import { streamSSE, speak } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { EVENTS, pickEvent } from "./SeasonBanner";

const seasonKey = (s) => {
  const mode = s?.banner_mode || "auto";
  if (mode === "av") return null;
  return EVENTS[mode] ? mode : pickEvent().key;
};

const seasonOffer = (s) => {
  const key = seasonKey(s);
  if (!key) return "";
  const offer = s?.season_offers?.[key];
  return offer ? `\n\n${EVENTS[key].eyebrow}: ${offer}` : "";
};

const welcome = (name, s) => `Hei, og velkommen til SømSignature! Jeg heter ${name} og er en AI-assistent. Jeg kan hjelpe deg å finne riktig tjeneste, forklare priser og hvordan du bestiller. Hva kan jeg hjelpe deg med?${seasonOffer(s)}`;
const CHIPS = ["Hva koster det å legge opp bukser?", "Jeg drømmer om en brudekjole", "Hvilke sykurs finnes?"];

const getSession = () => {
  let s = localStorage.getItem("ss_chat_session");
  if (!s) {
    s = `web-${crypto.randomUUID()}`;
    localStorage.setItem("ss_chat_session", s);
  }
  return s;
};

const Bubble = ({ m, onPlay, playing }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#1A1A1A] text-[#FDFBF7]" : "bg-[#F0EBE1] text-[#1A1A1A]"}`}>
      {m.content || <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
      {m.role === "assistant" && m.content && (
        <button onClick={onPlay} className="mt-2 flex items-center gap-1 text-[0.7rem] uppercase tracking-[0.15em] text-[#8C6E48] hover:text-[#1A1A1A]" data-testid="chat-play-message-btn">
          <Volume2 className={`w-3.5 h-3.5 ${playing ? "animate-pulse" : ""}`} strokeWidth={1.5} /> Lytt
        </button>
      )}
    </div>
  </div>
);

export const ChatWidget = () => {
  const settings = useContent()?.settings;
  const name = settings?.assistant_name || "Signe";
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: welcome("Signe") }]);
  useEffect(() => { setMsgs((m) => (m.length === 1 ? [{ role: "assistant", content: welcome(name, settings) }] : m)); }, [name, settings]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voice, setVoice] = useState(false);
  const [playing, setPlaying] = useState(-1);
  const endRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const play = async (text, idx) => {
    audioRef.current?.pause();
    setPlaying(idx);
    try {
      const a = await speak(text);
      audioRef.current = a;
      a.onended = () => setPlaying(-1);
    } catch {
      setPlaying(-1);
    }
  };

  const send = async (text) => {
    const t = text.trim();
    if (!t || busy) return;
    setInput("");
    setBusy(true);
    const idx = msgs.length + 1;
    setMsgs((m) => [...m, { role: "user", content: t }, { role: "assistant", content: "" }]);
    const update = (full) => setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, content: full } : x)));
    try {
      const full = await streamSSE("/chat/stream", { session_id: getSession(), message: t }, update);
      if (voice) play(full, idx);
    } catch (e) {
      update(e.message || "Beklager, noe gikk galt. Prøv igjen.");
    }
    setBusy(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-4 md:right-8 z-50 w-[calc(100vw-2rem)] max-w-[400px] h-[min(600px,75vh)] flex flex-col backdrop-blur-xl bg-[#FDFBF7]/90 border border-[#E5E0D8] shadow-2xl"
            data-testid="chat-window"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E0D8]">
              <div>
                <p className="font-serif-display text-xl leading-none" data-testid="chat-assistant-name">{name}</p>
                <p className="text-[0.65rem] tracking-[0.18em] uppercase text-[#8C6E48] mt-1">AI-assistent · SømSignature</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setVoice(!voice)} className={`p-2 transition-colors ${voice ? "text-[#8C6E48] pulse-gold" : "text-[#5C5A56]"}`} title="Opplesning av svar" data-testid="chat-voice-toggle">
                  {voice ? <Volume2 className="w-5 h-5" strokeWidth={1.5} /> : <VolumeX className="w-5 h-5" strokeWidth={1.5} />}
                </button>
                <button onClick={() => setOpen(false)} className="p-2 text-[#5C5A56] hover:text-[#1A1A1A]" data-testid="chat-close-btn"><X className="w-5 h-5" strokeWidth={1.5} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto slim-scroll px-4 py-4 space-y-3" data-testid="chat-messages">
              {msgs.map((m, i) => <Bubble key={i} m={m} playing={playing === i} onPlay={() => play(m.content, i)} />)}
              {msgs.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {[seasonKey(settings) && EVENTS[seasonKey(settings)].chip, ...CHIPS].filter(Boolean).map((c, i) => (
                    <button key={c} onClick={() => send(c)} className="text-xs px-3 py-2 border border-[#C4A47C] text-[#8C6E48] hover:bg-[#C4A47C] hover:text-white transition-colors" data-testid={`chat-chip-${i}`}>{c}</button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex border-t border-[#E5E0D8]">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Skriv meldingen din…" className="flex-1 bg-transparent px-4 py-4 text-sm outline-none" data-testid="chat-input" />
              <button type="submit" disabled={busy} className="px-5 text-[#1A1A1A] hover:text-[#8C6E48] disabled:opacity-40" data-testid="chat-send-btn"><Send className="w-4 h-4" strokeWidth={1.5} /></button>
            </form>
            <p className="px-4 py-2 text-[0.65rem] text-[#5C5A56] bg-[#F0EBE1]/70" data-testid="chat-disclaimer">
              Du snakker med en AI-assistent. Priser er veiledende – endelig pris og tidspunkt avtales alltid med Liisa.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-4 md:right-8 z-50 flex items-center gap-3 pl-4 pr-5 py-3.5 bg-[#1A1A1A] text-[#FDFBF7] shadow-xl hover:bg-[#8C6E48] transition-colors duration-300"
        data-testid="chat-open-btn"
      >
        <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
        <span className="text-sm tracking-wide">{open ? "Lukk" : `Spør ${name}`}</span>
      </button>
    </>
  );
};
